/* eslint-disable react-hooks/rules-of-hooks */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import type { useRouter } from "@tanstack/react-router";

import { appInstanceApi } from "@workspace/app-instance/api";
import {
	HttpRequestError,
	isHttpNotFound,
	isHttpStatus,
	isNotATenantMember,
	isTenantSuspended,
	setTenantSuspendedHandler,
} from "@workspace/base-api/fetchJson";

import {
	getCookieTenant,
	getStoredTenant,
	setCookieTenant,
	setStoredTenant,
} from "./storage";

export type TenantContext = ReturnType<typeof useTenantContext>;

export const TenantContext = React.createContext<TenantContext | null>(null);

export function getCurrentTenant(): string | null {
	return getCookieTenant() || getStoredTenant() || null;
}

export function useTenantContextWithStorage({
	userId,
	isAuthenticated,
	router,
	tenantMap = {},
	features,
	tenant: staticTenant,
}: {
	userId: string | null;
	isAuthenticated: boolean;
	router: ReturnType<typeof useRouter>;
	tenantMap: Record<string, string>;
	features?: string[];
	tenant?: string;
	url?: boolean;
}) {
	const [saved, setTenant] = React.useState<string | null>(
		() => getCookieTenant() || getStoredTenant() || null,
	);

	const tenant = staticTenant || saved;

	const tenantQuery = useQuery({
		...appInstanceApi.getOptionsCurrent(tenant!),
		enabled: !!tenant && isAuthenticated && !!userId,
		retry: 2,
	});

	const { data, refetch } = tenantQuery;

	const { data: allTenants = [] } = useQuery({
		...appInstanceApi.getForCurrentUserOptions(),
		enabled: isAuthenticated && !!userId,
		refetchInterval: import.meta.env.DEV ? 10000 : undefined,
		select: (d) => {
			return d.items.filter((item) => {
				if (!features?.length) return true;

				return item.features.some((feature) => features.includes(feature));
			});
		},
	});

	React.useEffect(() => {
		const fetchData = async () => {
			await refetch();
			await router.invalidate();
		};

		if (!tenant || !isAuthenticated || !userId) return;

		fetchData();

		return () => {
			router.invalidate();
		};
	}, [refetch, tenant, isAuthenticated, userId, router]);

	React.useEffect(() => {
		async function initTenant() {
			// Priority: cookie ? localStorage > URL mapping
			const cookieTenant = getCookieTenant();

			if (cookieTenant) {
				setTenant(cookieTenant);
				setStoredTenant(cookieTenant); // Sync localStorage with cookie
				setCookieTenant(cookieTenant);

				await router.invalidate();
				return;
			}

			const stored = getStoredTenant();

			if (stored) {
				setTenant(stored);
				setStoredTenant(stored);
				setCookieTenant(stored);

				await router.invalidate();
				return;
			}

			const currentUrl = window.location.origin;
			const urlTenant =
				tenantMap[currentUrl] ||
				(allTenants.length && allTenants[0]?.key) ||
				null;

			setTenant(urlTenant);
			setStoredTenant(urlTenant);
			setCookieTenant(urlTenant);

			await router.invalidate();
		}

		initTenant();

		return () => {
			router.invalidate();
		};
	}, [allTenants, router, tenantMap]);

	// HA-816: any request refused for a suspended membership drops the tenant too, not only this one.
	React.useEffect(() => {
		setTenantSuspendedHandler(() => {
			setCookieTenant(null);
			setStoredTenant(null);
			router.navigate({ reloadDocument: true });
		});
		return () => setTenantSuspendedHandler(null);
	}, [router]);

	React.useEffect(() => {
		if (tenantQuery.isError && tenantQuery.error instanceof HttpRequestError) {
			const error = tenantQuery.error;
			// HA-816: a tenant they are suspended in is dropped like one that is gone; HA-869: so is one
			// they don't belong to.
			if (
				isHttpNotFound(error) ||
				isTenantSuspended(error) ||
				isNotATenantMember(error)
			) {
				setCookieTenant(null);
				setStoredTenant(null);
				router.navigate({
					reloadDocument: true,
				});
			} else if (isHttpStatus(error, 401)) {
				router.navigate({
					to: "/login",
				});
			} else {
				console.error("Error fetching profile:", error);
			}
		}
	}, [tenantQuery.isError, tenantQuery.error, router]);

	return {
		tenant: data?.item?.key || tenant || undefined,
		root: data,
		allTenants,
		appInstance: data?.item || null,
		operations: data?.operations || [],
		setTenant: (newTenant: string) => {
			if (newTenant === tenant) return;

			setStoredTenant(newTenant);
			setCookieTenant(newTenant); // Also save to cookie
			setTenant(newTenant);
		},
	};
}

export function useTenantContextWithoutStorage({
	userId,
	isAuthenticated,
	router,
	tenantMap = {},
	features,
	tenant: staticTenant,
}: {
	userId: string | null;
	isAuthenticated: boolean;
	router: ReturnType<typeof useRouter>;
	tenantMap: Record<string, string>;
	features?: string[];
	tenant?: string;
}) {
	const [saved, setTenant] = React.useState<string | null>(null);

	const tenant = staticTenant || saved;

	const tenantQuery = useQuery({
		...appInstanceApi.getOptionsCurrent(tenant!),
		enabled: !!tenant && isAuthenticated && !!userId,
		retry: 2,
	});

	const { data, refetch } = tenantQuery;

	const { data: allTenants = [] } = useQuery({
		...appInstanceApi.getForCurrentUserOptions(),
		enabled: isAuthenticated && !!userId,
		refetchInterval: import.meta.env.DEV ? 10000 : undefined,
		select: (d) => {
			return d.items.filter((item) => {
				if (!features?.length) return true;

				return item.features.some((feature) => features.includes(feature));
			});
		},
	});

	React.useEffect(() => {
		const fetchData = async () => {
			await refetch();
			await router.invalidate();
		};

		if (!tenant || !isAuthenticated || !userId) return;

		fetchData();

		return () => {
			router.invalidate();
		};
	}, [refetch, tenant, isAuthenticated, userId, router]);

	React.useEffect(() => {
		return () => {
			router.invalidate();
		};
	}, [allTenants, router, tenantMap]);

	return {
		tenant: data?.item?.key || tenant || undefined,
		root: data,
		allTenants,
		appInstance: data?.item || null,
		operations: data?.operations || [],
		setTenant: (newTenant: string) => {
			if (newTenant === tenant) return;

			setTenant(newTenant);
		},
	};
}

export function useTenantContext({
	url,
	...props
}: {
	userId: string | null;
	isAuthenticated: boolean;
	router: ReturnType<typeof useRouter>;
	tenantMap: Record<string, string>;
	features?: string[];
	tenant?: string;
	url?: boolean;
}) {
	if (!url) {
		return useTenantContextWithStorage(props);
	} else {
		return useTenantContextWithoutStorage(props);
	}
}

export function useTenant() {
	const context = React.use(TenantContext);
	if (!context) {
		throw new Error("useTenant must be used within an TenantProvider");
	}
	return context;
}
